// TOUR-0 / B3.x — Firefly tour + ask contracts (doc-first types).
// See docs/planning/activity_dock_workflow_facilitation.md §10.1.

import type { ActivityDockAssistLevel } from "@/lib/activityDock/assistLevel";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import type { FireflyIntent } from "@/components/activity/firefly/fireflyIntents";
import type { BucketId } from "@/components/shell/modes/bucketsRegistry";

export type FireflyTourTrigger = "ask" | "discovery" | "recall" | "catalog";

export type FireflyInputModality = "text" | "voice";

export type FireflyPttTrigger = "long_press" | "double_click" | "hotkey" | "ask_mic";

export type FireflyTourRequestOutcome =
  | "started"
  | "denied_permission"
  | "not_found"
  | "cancelled"
  | "error";

export type FireflyTourRequestEventDetail = {
  tourId?: string;
  queryText?: string;
  trigger: FireflyTourTrigger;
  intent: Extract<FireflyIntent, "teaching" | "discovery" | "guidance">;
  mode: PrimaryMode;
  targetMode?: PrimaryMode;
  outcome: FireflyTourRequestOutcome;
  assistLevel: ActivityDockAssistLevel;
  ts: number;
  inputModality?: FireflyInputModality;
  pttTrigger?: FireflyPttTrigger;
};

/** ASK-1 MVP — KB/training artifact for text + optional navigation (not step-through). */
export type FireflyAskArtifact = {
  id: string;
  intents: string[];
  bucketRelevance: BucketId[];
  answer: string;
  navigateTo?: {
    mode: PrimaryMode;
    ctaLabel: string;
  };
  /** TOUR-2 — optional `data-firefly-target` to pulse after navigation. */
  highlightTarget?: string;
  /** Shipped tour id this ask complements (one primary ask per catalog tour). */
  companionTourId?: string;
  /** When set, caller must pass `hasRole` for at least one listed role. */
  requiredRoles?: string[];
};

export type FireflyAskResolution =
  | {
      kind: "answer";
      artifactId: string;
      answer: string;
      navigateTo?: { mode: PrimaryMode; ctaLabel: string };
      highlightTarget?: string;
      companionTourId?: string;
    }
  | {
      kind: "denied";
      artifactId: string;
      targetMode: PrimaryMode;
      message: string;
    }
  | { kind: "not_found"; message: string };

/** DOM / navigation target for one tour step (anchorId → `data-firefly-target`). */
export type FireflyTourTargetSurface = {
  mode: PrimaryMode;
  /** Stable target id — maps to `data-firefly-target` in the app shell. */
  anchorId: string;
  scrollIntoView?: boolean;
};

/** Role-specific copy override (B2.5 copy-lever pattern). */
export type FireflyTourStepCopy = {
  title?: string;
  body: string;
  roles?: string[];
};

/** B3.2 / TOUR-3 — one step in a KB tour artifact. */
export type FireflyTourStep = {
  id: string;
  target: FireflyTourTargetSurface;
  copy: FireflyTourStepCopy | FireflyTourStepCopy[];
  /**
   * When true, advance blocks until the user clicks the anchored control.
   * Ignored when `destructive` — those steps are describe-only (§9.3).
   */
  waitForClick?: boolean;
  advanceOn?: "next_button" | "timer";
  dwellMs?: number;
  /** Describe-only highlight; user must perform the action themselves. */
  destructive?: boolean;
  /** Skip this step when the user is already on the given mode (sidebar nav prelude). */
  skipWhenCurrentMode?: PrimaryMode;
  /** Highlight without auto-navigating to target.mode (mode-tab teaching steps). */
  deferModeNavigation?: boolean;
  /** Skip auto-injected bucket-switch prelude immediately before this step. */
  skipBucketPrelude?: boolean;
};

/** B3.2 — first-class KB tour artifact. */
export type FireflyTourArtifact = {
  id: string;
  intents: string[];
  bucketRelevance: BucketId[];
  requiredCapabilities?: string[];
  /** When set, caller must pass `hasRole` for at least one listed role. */
  requiredRoles?: string[];
  steps: FireflyTourStep[];
  defaultIntent: Extract<FireflyIntent, "teaching" | "guidance">;
  version: number;
  /**
   * When true (default), cross-bucket steps get runtime bucket-switch teaching
   * preludes before mode navigation / highlights.
   */
  guideBucketSwitch?: boolean;
};

export type FireflyTourResolution =
  | { kind: "start"; artifact: FireflyTourArtifact; targetMode: PrimaryMode }
  | {
      kind: "denied";
      reason: "no_access" | "no_match";
      targetMode?: PrimaryMode;
      message?: string;
    }
  | { kind: "error"; message: string };

export type FireflyTourParseResult =
  | { ok: true; artifact: FireflyTourArtifact }
  | { ok: false; errors: string[] };

export type FireflyTourRunnerStatus = "idle" | "running" | "completed" | "cancelled";

/** Runtime view of the active step — includes derived §9.3 guards. */
export type FireflyTourStepRuntime = {
  step: FireflyTourStep;
  index: number;
  /** Mirrors `waitForClick` unless step is describe-only (destructive). */
  waitForUserAction: boolean;
  /** Non-destructive default: destructive controls are highlight + copy only. */
  describeOnly: boolean;
  advanceOn: "next_button" | "timer";
  dwellMs: number;
};

export type FireflyTourRunnerState = {
  status: FireflyTourRunnerStatus;
  artifact: FireflyTourArtifact | null;
  steps: FireflyTourStep[];
  currentIndex: number;
  trigger: FireflyTourTrigger | null;
  intent: Extract<FireflyIntent, "teaching" | "discovery" | "guidance"> | null;
  currentStep: FireflyTourStepRuntime | null;
  /** Anti-Clippy: dismiss is always available while running. */
  canCancel: boolean;
  canAdvance: boolean;
};

export type FireflyTourRunnerEvent =
  | { type: "start"; artifact: FireflyTourArtifact; trigger: FireflyTourTrigger }
  | { type: "next" }
  | { type: "user_action" }
  | { type: "timer_elapsed" }
  | { type: "cancel" }
  | { type: "reset" };

export const FIREFLY_TOUR_DEFAULT_DWELL_MS = 8_000;

/** B3.6 — session lifecycle telemetry (start / complete / abandon). */
export type FireflyTourSessionPhase = "start" | "complete" | "abandon";

export type FireflyTourSessionEventDetail = {
  tourId: string;
  phase: FireflyTourSessionPhase;
  trigger: FireflyTourTrigger;
  intent: Extract<FireflyIntent, "teaching" | "discovery" | "guidance">;
  mode: PrimaryMode;
  stepIndex: number;
  stepCount: number;
  assistLevel: ActivityDockAssistLevel;
  ts: number;
};
